/**
 * API Configuration for Azure Backend
 *
 * This module provides centralized configuration for all API calls to Azure Functions.
 * During development, API calls are proxied through Vite to localhost:7071
 * In production, calls go directly to Azure Functions
 */

export const apiConfig = {
  // Base URL for API calls - set via environment variable
  // Local dev: empty string (uses Vite proxy)
  // Production: Function App URL (e.g., https://func-trimilo-dev.azurewebsites.net)
  baseURL: import.meta.env.VITE_AZURE_API_BASE_URL || '',

  // Default timeout for API calls (30 seconds)
  timeout: 30000,

  // Azure AD configuration (if using Azure AD B2C)
  auth: {
    clientId: import.meta.env.VITE_AZURE_AD_CLIENT_ID,
    tenantId: import.meta.env.VITE_AZURE_AD_TENANT_ID,
  },
};

// Log API configuration in development
if (import.meta.env.DEV) {
  console.log('🔧 API Configuration:', {
    baseURL: apiConfig.baseURL,
    environment: import.meta.env.MODE,
  });
}

/**
 * API Endpoints
 * Define all backend endpoints here for type safety and easy refactoring
 * All endpoints include /api prefix for both local and Azure Function Apps
 */
export const endpoints = {
  // Authentication
  auth: {
    register: '/api/auth/register',
    login: '/api/auth/login',
    refresh: '/api/auth/refresh',
    validate: '/api/auth/validate',
    me: '/api/auth/me',
  },

  // Trips
  trips: {
    list: '/api/trips',
    create: '/api/trips',
    get: (id: string) => `/api/trips/${id}`,
    update: (id: string) => `/api/trips/${id}`,
    delete: (id: string) => `/api/trips/${id}`,
    recalculate: (year: number) => `/api/trips/recalculate/${year}`,
  },

  // Reports
  reports: {
    list: '/api/reports',
    create: '/api/reports',
    generate: '/api/reports/generate',
    get: (id: string) => `/api/reports/${id}`,
    trips: (id: string) => `/api/reports/${id}/trips`,
    delete: (id: string) => `/api/reports/${id}`,
  },

  // User settings
  settings: {
    get: '/api/settings',
    update: '/api/settings',
  },

  // Fiscal years
  fiscalYears: {
    list: '/api/fiscal-years',
    create: '/api/fiscal-years',
    update: (id: string) => `/api/fiscal-years/${id}`,
    delete: (id: string) => `/api/fiscal-years/${id}`,
    months: (id: string) => `/api/fiscal-years/${id}/months`,
  },

  // Fiscal months
  fiscalMonths: {
    update: (id: string) => `/api/fiscal-months/${id}`,
  },

  // Saved addresses
  savedAddresses: {
    list: '/api/saved-addresses',
    create: '/api/saved-addresses',
    setDefault: (id: string) => `/api/saved-addresses/${id}/default`,
    updatePurpose: (id: string) => `/api/saved-addresses/${id}/purpose`,
    delete: (id: string) => `/api/saved-addresses/${id}`,
  },

  // Saved purposes
  savedPurposes: {
    list: '/api/saved-purposes',
    create: '/api/saved-purposes',
    delete: (id: string) => `/api/saved-purposes/${id}`,
  },

};
